import { getReferenceString, MedplumClient } from '@medplum/core';
import { Practitioner, Reference, Task } from '@medplum/fhirtypes';
import { UpsertPreprocedureTaskInput } from '@bariendo/lib/preprocedure-task';
import { BookAppointmentRequest } from '@bariendo/lib/scheduling';
import { fetchBot } from '@/services/services';
import { ET_TIMEZONE, PreprocedureTaskCode } from './constants';
import { LogisticalRow, toEt } from './rows';
import { TaskRef } from './types';

export interface SaveTaskArgs {
  patientId: string;
  code: PreprocedureTaskCode;
  status: Task['status'];
  existing?: TaskRef;
  note?: string;
  noteAuthor?: Reference<Practitioner>;
}

export function buildTaskInput({
  patientId,
  code,
  status,
  existing,
  note,
  noteAuthor,
}: SaveTaskArgs): UpsertPreprocedureTaskInput {
  return {
    patient: { reference: getReferenceString({ resourceType: 'Patient', id: patientId }) },
    taskCode: code,
    taskStatus: status,
    ...(existing && { taskId: existing.id }),
    ...(note !== undefined && { note }),
    ...(noteAuthor && { noteAuthor }),
  };
}

export async function saveTask(medplum: MedplumClient, args: SaveTaskArgs): Promise<TaskRef> {
  const task = await fetchBot<Task>(medplum, 'upsert-preprocedure-task', buildTaskInput(args));
  return {
    id: task.id as string,
    status: task.status,
    lastUpdated: task.meta?.lastUpdated ?? null,
    note: task.note?.[0]?.text ?? null,
  };
}

/** Same `book-appointment` update Retool's updateAppointmentNotes_medplum sends, with the date/time in ET. */
export function buildCommentPayload(
  row: LogisticalRow,
  comment: string,
  activityDefinitionId: string
): BookAppointmentRequest {
  const et = toEt(row.procedureDate);
  const text = comment.trim();
  return {
    appointment: { reference: getReferenceString({ resourceType: 'Appointment', id: row.appointmentId }) },
    activityDefinition: {
      reference: getReferenceString({ resourceType: 'ActivityDefinition', id: activityDefinitionId }),
      display: row.typeDisplay,
    },
    practitioner: row.providerId
      ? {
          reference: getReferenceString({ resourceType: 'Practitioner', id: row.providerId }),
          display: row.providerName ?? undefined,
        }
      : null,
    guests: [
      {
        reference: getReferenceString({ resourceType: 'Patient', id: row.patientId }),
        display: row.patientName ?? undefined,
      },
    ],
    date: et?.format('YYYY-MM-DD') ?? '',
    startTime: et?.format('HH:mm') ?? '',
    timezone: ET_TIMEZONE,
    visitType: { system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode', code: 'AMB', display: 'ambulatory' },
    location: row.locationId
      ? {
          reference: getReferenceString({ resourceType: 'Location', id: row.locationId }),
          display: row.locationName ?? undefined,
        }
      : undefined,
    notes: text || undefined,
    richTextNotes: text
      ? JSON.stringify({ type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text }] }] })
      : undefined,
  };
}

export async function saveAppointmentComment(
  medplum: MedplumClient,
  row: LogisticalRow,
  comment: string,
  activityDefinitionId: string
): Promise<void> {
  await fetchBot(medplum, 'book-appointment', buildCommentPayload(row, comment, activityDefinitionId));
}
